import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { RefreshCw, Truck, FileDown } from 'lucide-react'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'
import { Button } from '../components/ui/Button'
import { useActiveTenant } from '../contexts/tenant'

type CarrierGroup = { carrier: string; total: number; complete: number; semEtiqueta: number }

export function ExpeditionBoardPage() {
  const tenantId = useActiveTenant()
  const [busy, setBusy] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)

  const { data: groups = [], isLoading, refetch, isFetching } = useQuery<CarrierGroup[]>({
    queryKey: ['expedition-board', tenantId],
    queryFn: async () => (await api.get('/expedition/board', { params: { tenantId } })).data,
    enabled: !!tenantId,
  })

  if (!tenantId) {
    return <div className="flex h-64 items-center justify-center text-sm text-gray-500">Selecione um cliente no menu lateral para continuar.</div>
  }

  async function baixar(carrier: string) {
    setBusy(carrier); setMsg(null)
    try {
      const res = await api.post('/expedition/packet', { tenantId, carrier }, { responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }))
      const a = document.createElement('a')
      a.href = url; a.download = `expedicao-${carrier}.pdf`; a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      const count = Number(res.headers['x-packet-count'] ?? 0)
      const skipped = Number(res.headers['x-packet-skipped'] ?? 0)
      setMsg(`${carrier}: ${count} pedido(s) com etiqueta + DANFE${skipped ? ` · ${skipped} sem etiqueta (ML/Amazon?)` : ''}.`)
    } catch (e: any) {
      let m = 'Falha ao gerar o arquivo'
      try { m = JSON.parse(await e?.response?.data?.text?.() ?? '{}').title ?? m } catch { /* ignore */ }
      setMsg(`${carrier}: ${m}`)
    } finally { setBusy(null) }
  }

  const totalPedidos = groups.reduce((s, g) => s + g.total, 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Despachar</h2>
          <p className="text-sm text-gray-500">Pedidos a despachar agrupados por transportadora · etiqueta + DANFE por lote</p>
        </div>
        <Button variant="secondary" size="sm" loading={isFetching} onClick={() => refetch()}>
          <RefreshCw className="h-4 w-4" /> Atualizar
        </Button>
      </div>

      {msg && <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-2 text-sm text-blue-800">{msg}</div>}

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando...</p>
          ) : groups.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum pedido a despachar.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Transportadora</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600">Pedidos</th>
                    <th className="px-5 py-3 text-left font-medium text-gray-600">Completos</th>
                    <th className="px-5 py-3 text-right font-medium text-gray-600" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {groups.map(g => (
                    <tr key={g.carrier} className="hover:bg-gray-50">
                      <td className="px-5 py-3 font-medium text-gray-900">
                        <span className="inline-flex items-center gap-2"><Truck className="h-4 w-4 text-gray-400" /> {g.carrier}</span>
                      </td>
                      <td className="px-5 py-3 text-right font-semibold text-gray-900">{g.total}</td>
                      <td className="px-5 py-3">
                        <Badge variant={g.complete === g.total ? 'success' : 'warning'}>{g.complete}/{g.total} c/ etiqueta+NF</Badge>
                        {g.semEtiqueta > 0 && <span className="ml-2 text-xs text-amber-600">{g.semEtiqueta} sem etiqueta</span>}
                      </td>
                      <td className="px-5 py-3 text-right">
                        <Button size="sm" disabled={g.complete === 0} loading={busy === g.carrier} onClick={() => baixar(g.carrier)}>
                          <FileDown className="h-4 w-4" /> Etiquetas + DANFEs
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="border-t border-gray-100 bg-gray-50">
                  <tr>
                    <td className="px-5 py-3 text-sm font-medium text-gray-600">Total</td>
                    <td className="px-5 py-3 text-right font-bold text-gray-900">{totalPedidos}</td>
                    <td colSpan={2} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <p className="text-xs text-gray-400">
        Mercado Envios e Amazon DBA aparecem aqui, mas a etiqueta deles vem do marketplace (não do Melhor Envio) — por isso podem ficar "sem etiqueta" no arquivo automático.
      </p>
    </div>
  )
}
